// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, TextInput,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii, Shadow } from '../../theme/tokens';
import { useResponsive } from '../../theme/responsive';
import { useApp } from '../../store/AppStore';
import { ScreenShell } from '../../components/WebShell';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { Chip } from '../../components/Chip';
import ConfirmDialog from '../../components/ConfirmDialog';
import SuccessDialog from '../../components/SuccessDialog';

// Web-only staggered fade-up entrance.
function fadeUp(delayMs: number): any {
  if (Platform.OS !== 'web') return null;
  return {
    animationKeyframes: { '0%': { opacity: 0, transform: [{ translateY: 14 }] }, '100%': { opacity: 1, transform: [{ translateY: 0 }] } },
    animationDuration: '500ms', animationDelay: `${delayMs}ms`, animationFillMode: 'both',
    animationTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
  };
}

// ─── TagsFieldsScreen ─────────────────────────────────────────────────────────

const FIELD_TYPES = ['text', 'number', 'select', 'date', 'boolean'] as const;
const KEY_RE = /^[a-zA-Z0-9_]+$/;

export function TagsFieldsScreen() {
  const { orgSettings, saveOrgSettings, toast } = useApp();
  const { isMobile, pagePadX } = useResponsive();

  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');

  // Tag create / edit / delete → confirm → success flows.
  const [tagCreate, setTagCreate] = useState<string | null>(null);
  const [tagEdit, setTagEdit] = useState<{ original: string; value: string } | null>(null);
  const [tagEditConfirm, setTagEditConfirm] = useState(false);
  const [tagDelete, setTagDelete] = useState<string | null>(null);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagSuccess, setTagSuccess] = useState<string | null>(null);
  // Custom field create / edit / delete → confirm → success.
  const [fieldCreate, setFieldCreate] = useState<any | null>(null);
  const [fieldEdit, setFieldEdit] = useState<any | null>(null);
  const [fieldEditConfirm, setFieldEditConfirm] = useState(false);
  const [fieldDelete, setFieldDelete] = useState<string | null>(null);

  // Custom fields
  const [fields, setFields] = useState<any[]>([]);
  const [fKey, setFKey] = useState('');
  const [fLabel, setFLabel] = useState('');
  const [fType, setFType] = useState<typeof FIELD_TYPES[number]>('text');
  const [fRequired, setFRequired] = useState(false);
  const [fOptions, setFOptions] = useState('');

  // Sync tags from orgSettings
  useEffect(() => {
    if (orgSettings && Array.isArray(orgSettings.allowedTags)) {
      setTags(orgSettings.allowedTags);
    }
  }, [orgSettings]);

  // Sync custom fields from orgSettings
  useEffect(() => {
    if (orgSettings && Array.isArray(orgSettings.customFieldDefs)) {
      setFields(orgSettings.customFieldDefs);
    } else {
      setFields([]);
    }
  }, [orgSettings]);

  const resetFieldForm = () => {
    setFKey('');
    setFLabel('');
    setFType('text');
    setFRequired(false);
    setFOptions('');
  };

  // Validate the field form, then open the create confirmation.
  const requestAddField = () => {
    const key = fKey.trim();
    const label = fLabel.trim();
    if (!key) { toast('Field key is required.', 'danger', 'alert-triangle'); return; }
    if (key.length > 64) { toast('Field key must be 64 characters or fewer.', 'danger', 'alert-triangle'); return; }
    if (!KEY_RE.test(key)) { toast('Key may only contain letters, numbers and underscores.', 'danger', 'alert-triangle'); return; }
    if (fields.some(f => f.key === key)) { toast(`A field with key "${key}" already exists.`, 'danger', 'alert-triangle'); return; }
    if (!label) { toast('Field label is required.', 'danger', 'alert-triangle'); return; }
    if (label.length > 120) { toast('Field label must be 120 characters or fewer.', 'danger', 'alert-triangle'); return; }
    const def: any = { key, label, type: fType, required: fRequired };
    if (fType === 'select') {
      const options = fOptions.split(',').map(o => o.trim()).filter(Boolean);
      if (options.length === 0) { toast('Add at least one option for a select field.', 'danger', 'alert-triangle'); return; }
      def.options = options;
    }
    setFieldCreate(def);
  };

  const confirmAddField = async () => {
    if (!fieldCreate) return;
    setTagBusy(true);
    try {
      const updated = [...fields, fieldCreate];
      setFields(updated);
      resetFieldForm();
      await saveOrgSettings({ customFieldDefs: updated });
      const lbl = fieldCreate.label;
      setFieldCreate(null);
      setTagSuccess(`Field "${lbl}" created.`);
    } catch { toast('Failed to create field.', 'danger'); }
    finally { setTagBusy(false); }
  };

  // Open the field editor (key is immutable; label/type/required editable).
  const openFieldEdit = (f: any) => {
    setFieldEdit({ key: f.key, label: f.label, type: f.type, required: !!f.required, options: Array.isArray(f.options) ? f.options.join(', ') : '' });
  };

  const submitFieldEdit = async () => {
    if (!fieldEdit) return;
    const label = (fieldEdit.label || '').trim();
    if (!label) { toast('Field label is required.', 'danger'); return; }
    if (label.length > 120) { toast('Field label must be 120 characters or fewer.', 'danger'); return; }
    const def: any = { key: fieldEdit.key, label, type: fieldEdit.type, required: !!fieldEdit.required };
    if (fieldEdit.type === 'select') {
      const options = (fieldEdit.options || '').split(',').map((o: string) => o.trim()).filter(Boolean);
      if (options.length === 0) { toast('Add at least one option for a select field.', 'danger'); return; }
      def.options = options;
    }
    setTagBusy(true);
    try {
      const updated = fields.map(f => (f.key === fieldEdit.key ? def : f));
      setFields(updated);
      await saveOrgSettings({ customFieldDefs: updated });
      setFieldEditConfirm(false);
      setFieldEdit(null);
      setTagSuccess(`Field "${label}" updated.`);
    } catch { toast('Failed to update field.', 'danger'); }
    finally { setTagBusy(false); }
  };

  const confirmRemoveField = async () => {
    if (!fieldDelete) return;
    setTagBusy(true);
    try {
      const removed = fieldDelete;
      const updated = fields.filter(f => f.key !== removed);
      setFields(updated);
      await saveOrgSettings({ customFieldDefs: updated });
      setFieldDelete(null);
      setTagSuccess(`Field "${removed}" removed.`);
    } catch { toast('Failed to remove field.', 'danger'); }
    finally { setTagBusy(false); }
  };

  // Validate then open the create confirmation.
  const requestAddTag = () => {
    const t = newTag.trim();
    if (!t) return;
    if (tags.includes(t)) { toast(`Tag ${t} already exists.`, 'warn'); return; }
    setTagCreate(t);
  };

  const confirmAddTag = async () => {
    if (!tagCreate) return;
    setTagBusy(true);
    try {
      const t = tagCreate;
      const updated = [...tags, t];
      setTags(updated);
      setNewTag('');
      await saveOrgSettings({ allowedTags: updated });
      setTagCreate(null);
      setTagSuccess(`Tag "${t}" created.`);
    } catch { toast('Failed to create tag.', 'danger'); }
    finally { setTagBusy(false); }
  };

  // Rename a tag (edit → confirm → success).
  const submitTagEdit = async () => {
    if (!tagEdit) return;
    const value = tagEdit.value.trim().toLowerCase().replace(/\s+/g, '-');
    if (!value) { setTagEdit(null); return; }
    if (value !== tagEdit.original && tags.includes(value)) {
      toast(`Tag "${value}" already exists.`, 'warn'); return;
    }
    setTagBusy(true);
    try {
      const updated = tags.map(t => (t === tagEdit.original ? value : t));
      setTags(updated);
      await saveOrgSettings({ allowedTags: updated });
      setTagEditConfirm(false);
      setTagEdit(null);
      setTagSuccess(`Tag renamed to "${value}".`);
    } catch { toast('Failed to update tag.', 'danger'); }
    finally { setTagBusy(false); }
  };

  // Delete a tag (delete → confirm → success).
  const confirmTagDelete = async () => {
    if (!tagDelete) return;
    setTagBusy(true);
    try {
      const removed = tagDelete;
      const updated = tags.filter(x => x !== removed);
      setTags(updated);
      await saveOrgSettings({ allowedTags: updated });
      setTagDelete(null);
      setTagSuccess(`Tag "${removed}" deleted.`);
    } catch { toast('Failed to delete tag.', 'danger'); }
    finally { setTagBusy(false); }
  };

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Tags & fields' },
    ]}>
      <View style={styles.root}>
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>Tags & fields</Text>
            <Text style={styles.pageSub}>Tags categorise tickets and contacts. Custom fields capture structured data on every ticket.</Text>
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 24 }]}>
          {/* Tags */}
          <Text style={styles.sectionTitle}>Tags</Text>
          <Text style={styles.sectionSub}>Used to group tickets and contacts. Suggestion: keep the set small and clean.</Text>

          <View style={[styles.card, { marginTop: 8 }, fadeUp(40)]}>
            <View style={styles.cardRow}>
              <View style={styles.addTagRow}>
                <TextInput
                  style={[styles.input, { flex: 1 }]}
                  placeholder="new-tag-name"
                  value={newTag}
                  onChangeText={v => setNewTag(v.toLowerCase().replace(/\s+/g, '-'))}
                  onSubmitEditing={requestAddTag}
                  returnKeyType="done"
                />
                <PrimaryButton
                  title="Add tag"
                  variant="primary"
                  size="md"
                  leftIcon={<Icon name="Plus" size={13} color={Colors.paper} />}
                  onPress={requestAddTag}
                />
              </View>
            </View>
            <View style={[styles.cardRow, styles.cardRowLast]}>
              {tags.length === 0 ? (
                <Text style={styles.emptyText}>No tags yet.</Text>
              ) : (
                <View style={styles.tagsList}>
                  {tags.map(t => (
                    <View key={t} style={styles.tagChip}>
                      <View style={styles.tagDot} />
                      <Text style={styles.tagName}>{t}</Text>
                      <TouchableOpacity onPress={() => setTagEdit({ original: t, value: t })} hitSlop={{ top: 6, bottom: 6, left: 3, right: 2 }} accessibilityLabel={`Edit tag ${t}`}>
                        <Icon name="Edit" size={11} color={Colors.ink4} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => setTagDelete(t)} hitSlop={{ top: 6, bottom: 6, left: 2, right: 4 }} accessibilityLabel={`Delete tag ${t}`}>
                        <Icon name="Trash" size={11} color={Colors.ink4} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>

          {/* Custom Fields */}
          <Text style={[styles.sectionTitle, { marginTop: 8 }]}>Custom ticket fields</Text>
          <Text style={styles.sectionSub}>Capture extra structured data on every ticket. Surfaced in the right panel and API.</Text>

          <View style={[styles.card, { marginTop: 8 }, fadeUp(120)]}>
            {/* Existing fields */}
            <View style={styles.cardRow}>
              {fields.length === 0 ? (
                <Text style={styles.emptyText}>No custom fields yet.</Text>
              ) : (
                <View style={styles.fieldsList}>
                  {fields.map(f => (
                    <View key={f.key} style={styles.fieldRow}>
                      <View style={{ flex: 1 }}>
                        <View style={styles.fieldHeadRow}>
                          <Text style={styles.fieldLabel}>{f.label}</Text>
                          <Chip label={f.type} tone="default" size="sm" />
                          {f.required ? <Chip label="required" tone="accent" size="sm" /> : null}
                        </View>
                        <Text style={styles.fieldKey}>{f.key}</Text>
                        {f.type === 'select' && Array.isArray(f.options) && f.options.length > 0 ? (
                          <Text style={styles.fieldOptions}>Options: {f.options.join(', ')}</Text>
                        ) : null}
                      </View>
                      <View style={styles.fieldActions}>
                        <TouchableOpacity onPress={() => openFieldEdit(f)} style={styles.fieldActBtn} accessibilityLabel={`Edit field ${f.label}`}>
                          <Icon name="Edit" size={14} color={Colors.ink3} />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setFieldDelete(f.key)} style={styles.fieldActBtn} accessibilityLabel={`Delete field ${f.label}`}>
                          <Icon name="Trash" size={14} color={Colors.ink3} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>

            {/* Add field */}
            <View style={[styles.cardRow, styles.fieldFormBox]}>
              <Text style={styles.formTitle}>Add a field</Text>

              <View style={[styles.formGrid, isMobile && { flexDirection: 'column' }]}>
                <View style={styles.formField}>
                  <Text style={styles.fieldFormLabel}>Key</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="order_id"
                    autoCapitalize="none"
                    value={fKey}
                    onChangeText={v => setFKey(v.replace(/[^a-zA-Z0-9_]/g, ''))}
                  />
                </View>
                <View style={styles.formField}>
                  <Text style={styles.fieldFormLabel}>Label</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Order ID"
                    value={fLabel}
                    onChangeText={setFLabel}
                  />
                </View>
              </View>

              <View style={{ marginTop: 10 }}>
                <Text style={styles.fieldFormLabel}>Type</Text>
                <View style={styles.typeRow}>
                  {FIELD_TYPES.map(t => (
                    <TouchableOpacity
                      key={t}
                      onPress={() => setFType(t)}
                      style={[styles.typeChip, fType === t && styles.typeChipActive]}
                    >
                      <Text style={[styles.typeChipText, fType === t && styles.typeChipTextActive]}>{t}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {fType === 'select' ? (
                <View style={{ marginTop: 10 }}>
                  <Text style={styles.fieldFormLabel}>Options (comma-separated)</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Low, Medium, High"
                    value={fOptions}
                    onChangeText={setFOptions}
                  />
                </View>
              ) : null}

              <TouchableOpacity
                style={styles.requiredRow}
                onPress={() => setFRequired(r => !r)}
                activeOpacity={0.7}
              >
                <View style={[styles.checkbox, fRequired && styles.checkboxOn]}>
                  {fRequired ? <Icon name="Check" size={11} color={Colors.paper} /> : null}
                </View>
                <Text style={styles.requiredText}>Required field</Text>
              </TouchableOpacity>

              <View style={{ flexDirection: 'row', marginTop: 12 }}>
                <PrimaryButton
                  title="Add field"
                  variant="primary"
                  size="md"
                  leftIcon={<Icon name="Plus" size={13} color={Colors.paper} />}
                  onPress={requestAddField}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      </View>

      {/* Tag create → confirm */}
      <ConfirmDialog
        visible={tagCreate !== null}
        title="Create tag?"
        message={tagCreate ? `Add the tag "${tagCreate}" to your allowed set?` : ''}
        confirmText="Yes, create"
        cancelText="Cancel"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={confirmAddTag}
        onCancel={() => { if (!tagBusy) setTagCreate(null); }}
      />

      {/* Field create → confirm */}
      <ConfirmDialog
        visible={fieldCreate !== null}
        title="Create field?"
        message={fieldCreate ? `Add the custom field "${fieldCreate.label}" (${fieldCreate.type}${fieldCreate.required ? ', required' : ''}) to every ticket?` : ''}
        confirmText="Yes, create"
        cancelText="Cancel"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={confirmAddField}
        onCancel={() => { if (!tagBusy) setFieldCreate(null); }}
      />

      {/* Field edit card */}
      <ConfirmDialog
        visible={fieldEdit !== null}
        title="Edit field"
        message="Update the field’s label, type or required flag. The key can’t change."
        confirmText="Save"
        cancelText="Cancel"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={() => { if ((fieldEdit?.label ?? '').trim()) setFieldEditConfirm(true); }}
        onCancel={() => { if (!tagBusy) setFieldEdit(null); }}
        onClose={() => { if (!tagBusy) setFieldEdit(null); }}
      >
        {fieldEdit && (
          <View style={{ width: '100%', marginTop: 6, gap: 14 }}>
            <View>
              <Text style={styles.dialogFieldLabel}>Key</Text>
              <Text style={styles.dialogKeyLocked}>{fieldEdit.key}  ·  locked</Text>
            </View>
            <View>
              <Text style={styles.dialogFieldLabel}>Label</Text>
              <TextInput
                style={styles.dialogInput}
                value={fieldEdit.label}
                autoFocus
                onChangeText={(v) => setFieldEdit((d: any) => ({ ...d, label: v }))}
                placeholder="Order ID"
                placeholderTextColor={Colors.ink4}
              />
            </View>
            <View>
              <Text style={styles.dialogFieldLabel}>Type</Text>
              <View style={styles.typeRow}>
                {FIELD_TYPES.map(t => (
                  <TouchableOpacity key={t} onPress={() => setFieldEdit((d: any) => ({ ...d, type: t }))}
                    style={[styles.typeChip, fieldEdit.type === t && styles.typeChipActive]}>
                    <Text style={[styles.typeChipText, fieldEdit.type === t && styles.typeChipTextActive]}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            {fieldEdit.type === 'select' && (
              <View>
                <Text style={styles.dialogFieldLabel}>Options (comma-separated)</Text>
                <TextInput
                  style={styles.dialogInput}
                  value={fieldEdit.options}
                  onChangeText={(v) => setFieldEdit((d: any) => ({ ...d, options: v }))}
                  placeholder="Low, Medium, High"
                  placeholderTextColor={Colors.ink4}
                />
              </View>
            )}
            <TouchableOpacity style={styles.requiredRow} onPress={() => setFieldEdit((d: any) => ({ ...d, required: !d.required }))} activeOpacity={0.7}>
              <View style={[styles.checkbox, fieldEdit.required && styles.checkboxOn]}>
                {fieldEdit.required ? <Icon name="Check" size={11} color={Colors.paper} /> : null}
              </View>
              <Text style={styles.requiredText}>Required field</Text>
            </TouchableOpacity>
          </View>
        )}
      </ConfirmDialog>

      {/* Field edit → save confirmation */}
      <ConfirmDialog
        visible={fieldEditConfirm}
        title="Update field?"
        message={fieldEdit ? `Save changes to the "${(fieldEdit.label || '').trim()}" field across every ticket?` : ''}
        confirmText="Yes, update"
        cancelText="Go back"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={submitFieldEdit}
        onCancel={() => { if (!tagBusy) setFieldEditConfirm(false); }}
      />

      {/* Field delete → confirm */}
      <ConfirmDialog
        visible={fieldDelete !== null}
        title="Delete field?"
        message={fieldDelete ? `The custom field "${fieldDelete}" will be removed from all tickets.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        loading={tagBusy}
        onConfirm={confirmRemoveField}
        onCancel={() => { if (!tagBusy) setFieldDelete(null); }}
      />

      {/* Tag edit → confirm → save */}
      <ConfirmDialog
        visible={tagEdit !== null}
        title="Edit tag"
        message="Rename this tag. It updates everywhere the tag is used."
        confirmText="Save"
        cancelText="Cancel"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={() => { if ((tagEdit?.value ?? '').trim()) setTagEditConfirm(true); }}
        onCancel={() => { if (!tagBusy) setTagEdit(null); }}
        onClose={() => { if (!tagBusy) setTagEdit(null); }}
      >
        <View style={{ width: '100%', marginTop: 6 }}>
          <TextInput
            style={styles.dialogInput}
            value={tagEdit?.value ?? ''}
            autoFocus
            onChangeText={(v) => setTagEdit(d => (d ? { ...d, value: v.toLowerCase().replace(/\s+/g, '-') } : d))}
            onSubmitEditing={submitTagEdit}
            placeholder="tag-name"
            placeholderTextColor={Colors.ink4}
          />
        </View>
      </ConfirmDialog>

      {/* Tag edit → save confirmation */}
      <ConfirmDialog
        visible={tagEditConfirm}
        title="Update tag?"
        message={tagEdit ? `Rename "${tagEdit.original}" to "${(tagEdit.value || '').trim().toLowerCase().replace(/\s+/g, '-')}"? It updates everywhere the tag is used.` : ''}
        confirmText="Yes, update"
        cancelText="Go back"
        confirmColor={Colors.accent}
        loading={tagBusy}
        onConfirm={submitTagEdit}
        onCancel={() => { if (!tagBusy) setTagEditConfirm(false); }}
      />

      {/* Tag delete → confirm */}
      <ConfirmDialog
        visible={tagDelete !== null}
        title="Delete tag?"
        message={tagDelete ? `The tag "${tagDelete}" will be removed from the allowed set.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        loading={tagBusy}
        onConfirm={confirmTagDelete}
        onCancel={() => { if (!tagBusy) setTagDelete(null); }}
      />

      <SuccessDialog
        visible={tagSuccess !== null}
        title="Done"
        message={tagSuccess ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setTagSuccess(null) }}
        onClose={() => setTagSuccess(null)}
      />
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  body: { flex: 1 },
  bodyContent: { maxWidth: 1000, alignSelf: 'center', width: '100%' as any, padding: 24 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 2 },
  card: {
    backgroundColor: Colors.card, borderRadius: Radii.r4,
    borderWidth: 1, borderColor: Colors.line,
    marginBottom: 16, overflow: 'hidden',
    ...(Shadow.base as object),
  },
  cardRow: { padding: 18 },
  cardRowLast: {},
  addTagRow: { flexDirection: 'row', gap: 10 },
  tagsList: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  tagChip: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    paddingLeft: 11, paddingRight: 9, paddingVertical: 6,
    borderRadius: Radii.full, borderWidth: 1, borderColor: Colors.accentLine,
    backgroundColor: Colors.accentSoft,
    ...(Shadow.sm as object),
  },
  tagDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: Colors.accent },
  tagName: { fontSize: 12.5, fontWeight: FontWeight.semibold, color: Colors.accentInk },
  dialogInput: {
    width: '100%',
    borderWidth: 1, borderColor: Colors.lineStrong,
    borderRadius: Radii.r3, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: FontSize.md, color: Colors.ink, backgroundColor: Colors.card,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none', boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.04)' } as any : {}),
  },
  emptyText: { fontSize: 12, color: Colors.ink4 },
  input: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 10, paddingVertical: 7,
    fontSize: 13.5, color: Colors.ink, backgroundColor: Colors.card,
  },

  // Custom fields — list
  fieldsList: { gap: 8 },
  fieldRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    paddingVertical: 10, paddingHorizontal: 12,
    borderRadius: Radii.r2, borderWidth: 1, borderColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
  fieldHeadRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  fieldLabel: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },
  fieldKey: { fontSize: 12, color: Colors.ink4, marginTop: 2, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
  fieldOptions: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  fieldRemove: { padding: 4 },
  fieldActions: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  fieldActBtn: {
    width: 30, height: 30, borderRadius: Radii.r2, alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  dialogFieldLabel: { fontSize: 12, fontWeight: FontWeight.semibold, color: Colors.ink2, marginBottom: 6, textAlign: 'left' },
  dialogKeyLocked: {
    fontSize: 12.5, color: Colors.ink4,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
    backgroundColor: Colors.paper2, borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r2,
    paddingHorizontal: 10, paddingVertical: 8, alignSelf: 'flex-start',
  },

  // Custom fields — add form
  fieldFormBox: { borderTopWidth: 1, borderTopColor: Colors.line },
  formTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink, marginBottom: 10 },
  formGrid: { flexDirection: 'row', gap: 10, flexWrap: 'wrap' },
  formField: { flex: 1, minWidth: 160 },
  fieldFormLabel: { fontSize: 12, color: Colors.ink3, marginBottom: 4 },
  typeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 2 },
  typeChip: {
    paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: Radii.full, borderWidth: 1, borderColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
  typeChipActive: { borderColor: Colors.accent, backgroundColor: Colors.accent },
  typeChipText: { fontSize: 12.5, color: Colors.ink3 },
  typeChipTextActive: { color: Colors.paper, fontWeight: FontWeight.semibold },
  requiredRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 },
  checkbox: {
    width: 18, height: 18, borderRadius: Radii.r1,
    borderWidth: 1, borderColor: Colors.line,
    alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.card,
  },
  checkboxOn: { borderColor: Colors.accent, backgroundColor: Colors.accent },
  requiredText: { fontSize: 13, color: Colors.ink },
});
